import { HOUR_S, MINUTE_S, SECOND_MS } from '@calm/web-common/dist/time';
import commonMessages from '@rhg/lang/messages/common';
import { displayLocaleDateString } from '@rhg/utils/date-time';
import { isToday, isYesterday } from 'date-fns';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

export interface HumanReadableDurationOptions {
    /** Render whole hours as "{n} hrs". */
    hours?: boolean;
    /** Render minutes as "{n} min" (remaining minutes when hours is also set). */
    minutes?: boolean;
    /** Render seconds as "{n} sec" (remaining seconds when minutes or hours is also set). */
    seconds?: boolean;
}

const DURATION_PART_SEPARATOR = ', ';

export function useHumanReadable() {
    const { formatMessage } = useIntl();

    const roundedDuration = useCallback((ms?: number) => {
        if (typeof ms === 'undefined') {
            return '';
        }

        const seconds = Math.round(ms / SECOND_MS);
        const minutes = Math.round(seconds / 60);

        return minutes.toString();
    }, []);

    const duration = useCallback(
        (ms?: number, options?: HumanReadableDurationOptions) => {
            if (typeof ms === 'undefined') {
                return '';
            }

            const showHours = Boolean(options?.hours);
            const showMinutes = Boolean(options?.minutes);
            const showSeconds = Boolean(options?.seconds);
            const totalSeconds = Math.floor(Math.max(0, ms) / SECOND_MS);

            if (!showHours && !showMinutes && !showSeconds) {
                const minutes = Math.floor(totalSeconds / MINUTE_S);
                if (minutes) {
                    return formatMessage(commonMessages['common.min'], {
                        minutes,
                    });
                }
                return formatMessage(commonMessages['common.sec'], {
                    seconds: totalSeconds,
                });
            }

            const hours = Math.floor(totalSeconds / HOUR_S);
            const minutes = showHours
                ? Math.floor((totalSeconds % HOUR_S) / MINUTE_S)
                : Math.floor(totalSeconds / MINUTE_S);
            const seconds = showMinutes
                ? totalSeconds % MINUTE_S
                : showHours
                ? totalSeconds % HOUR_S
                : totalSeconds;

            const parts: string[] = [];
            if (showHours) {
                parts.push(
                    formatMessage(commonMessages['common.hrs'], {
                        hours,
                    })
                );
            }
            if (showMinutes) {
                parts.push(
                    formatMessage(commonMessages['common.min'], {
                        minutes,
                    })
                );
            }
            if (showSeconds) {
                parts.push(
                    formatMessage(commonMessages['common.sec'], {
                        seconds,
                    })
                );
            }
            return parts.join(DURATION_PART_SEPARATOR);
        },
        [formatMessage]
    );

    const dateAndRelativeDay = useCallback(
        (date: Date) =>
            (isYesterday(date)
                ? formatMessage(commonMessages['common.yesterday']) + ', '
                : '') +
            (isToday(date)
                ? formatMessage(commonMessages['common.today']) + ', '
                : '') +
            displayLocaleDateString(date),
        [formatMessage]
    );

    return {
        duration,
        dateAndRelativeDay,
        roundedDuration,
    };
}
